/**
 * Mask Outline Utilities
 *
 * Pure utility functions for detecting and drawing outlines at the
 * alpha boundary of a PNG mask image on an HTML Canvas.
 *
 * Usage flow:
 *   1. drawMaskOutlineOnCanvas({ ctx, maskImage, positioning })
 *      — high-level helper that handles the full pipeline
 *
 * Or manually:
 *   1. extractAlphaEdge({ maskCanvas, threshold })
 *   2. drawEdgeOutline({ ctx, edgePixels, color, lineWidth, smooth })
 */

import type { PNGMaskPositioning } from './pngProcessor';

export const MASK_COLOUR = '#000000';
export const MASK_THICKNESS = 5;

interface EdgePixel {
  x: number;
  y: number;
}

/**
 * Scans the mask's alpha channel and returns every pixel that sits
 * on the boundary between opaque and transparent regions.
 *
 * A pixel is an edge pixel when it is opaque (>= threshold) and at
 * least one of its 4-connected neighbours is transparent (< threshold)
 * or is outside the canvas bounds.
 */
export function extractAlphaEdge(options: { maskCanvas: HTMLCanvasElement; threshold?: number }): EdgePixel[] {
  const { maskCanvas, threshold = 128 } = options;
  const { width, height } = maskCanvas;
  const ctx = maskCanvas.getContext('2d');
  if (!ctx) return [];

  const { data } = ctx.getImageData(0, 0, width, height);
  const edgePixels: EdgePixel[] = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha < threshold) continue;

      const atEdge =
        x === 0 ||
        x === width - 1 ||
        y === 0 ||
        y === height - 1 ||
        data[(y * width + (x - 1)) * 4 + 3] < threshold ||
        data[(y * width + (x + 1)) * 4 + 3] < threshold ||
        data[((y - 1) * width + x) * 4 + 3] < threshold ||
        data[((y + 1) * width + x) * 4 + 3] < threshold;

      if (atEdge) {
        edgePixels.push({ x, y });
      }
    }
  }

  return edgePixels;
}

/**
 * Draws a solid outline on a canvas at the given edge pixel positions.
 */
export function drawEdgeOutline(options: {
  ctx: CanvasRenderingContext2D;
  edgePixels: EdgePixel[];
  color?: string;
  lineWidth?: number;
}): void {
  const { ctx, edgePixels, color = MASK_COLOUR, lineWidth = MASK_THICKNESS } = options;

  if (edgePixels.length === 0) return;

  const { width, height } = ctx.canvas;
  const edgeCanvas = document.createElement('canvas');
  edgeCanvas.width = width;
  edgeCanvas.height = height;
  const ectx = edgeCanvas.getContext('2d');
  if (!ectx) return;

  ectx.fillStyle = color;
  const half = Math.floor(lineWidth / 2);

  for (const { x, y } of edgePixels) {
    ectx.fillRect(x - half, y - half, lineWidth, lineWidth);
  }

  ctx.drawImage(edgeCanvas, 0, 0);
}

/**
 * High-level function: draws the mask outline onto the target canvas.
 *
 * Creates a temp canvas matching the output size, draws the mask at
 * the correct position/scale, extracts the alpha edge, then draws
 * the solid outline on the target context.
 */
export function drawMaskOutlineOnCanvas(options: {
  ctx: CanvasRenderingContext2D;
  maskImage: HTMLImageElement;
  positioning: PNGMaskPositioning;
  color?: string;
  lineWidth?: number;
}): void {
  const { ctx, maskImage, positioning, color, lineWidth } = options;

  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = canvasWidth;
  maskCanvas.height = canvasHeight;
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return;

  const maskAreaWidth = canvasWidth * positioning.maskScalePercent;
  const maskAspectRatio = maskImage.height / maskImage.width;
  const maskAreaHeight = maskAreaWidth * maskAspectRatio;

  const maskAreaX = positioning.maskCenterX - maskAreaWidth / 2;
  const maskAreaY = positioning.maskCenterY - maskAreaHeight / 2;

  maskCtx.drawImage(maskImage, maskAreaX, maskAreaY, maskAreaWidth, maskAreaHeight);

  const edgePixels = extractAlphaEdge({ maskCanvas });

  drawEdgeOutline({ ctx, edgePixels, color, lineWidth });
}
